/**
 * Generic Mystery Pack buy box.
 *
 * Required product options: Size + Pack Size.
 * Pool: custom.mystery_pack_pool.
 * Color priority: custom.mystery_pack_color_priority.
 * Fill rule: custom.mystery_pack_fill_rule
 * (broken_colorways | any_in_stock | most_units).
 */
class MysteryPackForm extends HTMLElement {
  connectedCallback() {
    if (this._mysteryPackReady) return;
    this._mysteryPackReady = true;

    this.core = window.MysteryPackCore;
    if (!this.core) {
      console.warn("MysteryPackForm: core helpers are unavailable");
      return;
    }

    this.fillRule = this.core.normalizeFillRule(this.dataset.fillRule);
    this.pool = this.readJson("[data-mystery-pack-pool]");
    this.packVariants = this.readJson("[data-mystery-pack-variants]");
    this.colorPriority = this.core.normalizeColorPriority(
      this.readJson("[data-mystery-pack-color-priority]"),
    );

    this.form = this.querySelector("form");
    this.sizeInputs = Array.from(
      this.querySelectorAll('[data-mystery-pack-option="size"]'),
    );
    this.countInputs = Array.from(
      this.querySelectorAll('[data-mystery-pack-option="pack-size"]'),
    );
    this.variantSelect = this.querySelector("[data-mystery-pack-variant-select]");
    this.variantsInput = this.querySelector("[data-mystery-pack-props]");
    this.countInput = this.querySelector("[data-mystery-pack-count]");
    this.uidInput = this.querySelector("[data-mystery-pack-uid]");
    this.quantityInput = this.querySelector("[data-mystery-pack-qty]");
    this.addButton = this.querySelector("[data-mystery-pack-add]");
    this.addButtonText = this.querySelector("[data-mystery-pack-add-text]");
    this.errorsEl = this.querySelector("[data-mystery-pack-errors]");
    this.priceEl = document.querySelector(
      '[data-mystery-pack-price-for="' + this.dataset.sectionId + '"]',
    );
    this.comparePriceEl = document.querySelector(
      '[data-mystery-pack-compare-price-for="' + this.dataset.sectionId + '"]',
    );

    this.availability = this.core.buildAvailability(
      this.pool,
      this.sizeInputs.map(function (input) {
        return input.value;
      }),
      this.fillRule,
      this.colorPriority,
    );

    this.bindEvents();
    this.syncSelection();
  }

  readJson(selector) {
    var element = this.querySelector(selector);
    if (!element) return [];

    try {
      return JSON.parse(element.textContent.trim() || "[]");
    } catch (error) {
      console.warn("MysteryPackForm: invalid JSON", selector, error);
      return [];
    }
  }

  bindEvents() {
    this.sizeInputs.concat(this.countInputs).forEach(
      function (input) {
        input.addEventListener(
          "change",
          function () {
            this.clearErrors();
            this.syncSelection();
          }.bind(this),
        );
      }.bind(this),
    );

    if (this.form) {
      this.form.addEventListener(
        "submit",
        function (event) {
          event.preventDefault();
          this.handleAddToCart();
        }.bind(this),
      );
    }
  }

  getCheckedInput(inputs) {
    return inputs.find(function (input) {
      return input.checked && !input.disabled;
    }) || null;
  }

  getSelectedPackCount() {
    var input = this.getCheckedInput(this.countInputs);
    return input ? this.core.parsePackCount(input.value) : 0;
  }

  isCombinationAvailable(sizeValue, packCount) {
    var sizeKey = this.core.normalizeSize(sizeValue);
    var info = this.availability[sizeKey] || { count: 0 };
    var packVariant = this.core.findPackVariant(
      this.packVariants,
      sizeValue,
      packCount,
    );

    return Boolean(
      packVariant && packVariant.available && info.count >= packCount,
    );
  }

  setInputAvailability(input, available, candidateCount) {
    var wrapper = input.closest(".variant-input");
    var label = wrapper && wrapper.querySelector("label");

    input.disabled = !available;
    input.classList.toggle("disabled", !available);
    if (candidateCount != null) {
      input.setAttribute("data-pool-count", String(candidateCount));
    }
    if (label) label.classList.toggle("disabled", !available);
    if (!available && input.checked) input.checked = false;
  }

  applyPackCountAvailability() {
    this.countInputs.forEach(
      function (input) {
        var packCount = this.core.parsePackCount(input.value);
        var available = this.sizeInputs.some(
          function (sizeInput) {
            return this.isCombinationAvailable(sizeInput.value, packCount);
          }.bind(this),
        );
        this.setInputAvailability(input, available);
      }.bind(this),
    );

    if (!this.getCheckedInput(this.countInputs)) {
      var firstAvailableCount = this.countInputs.find(function (input) {
        return !input.disabled;
      });
      if (firstAvailableCount) firstAvailableCount.checked = true;
    }
  }

  applySizeAvailability(packCount) {
    this.sizeInputs.forEach(
      function (input) {
        var sizeKey = this.core.normalizeSize(input.value);
        var info = this.availability[sizeKey] || { count: 0 };
        this.setInputAvailability(
          input,
          this.isCombinationAvailable(input.value, packCount),
          info.count,
        );
      }.bind(this),
    );

    if (!this.getCheckedInput(this.sizeInputs)) {
      var firstAvailableSize = this.sizeInputs.find(function (input) {
        return !input.disabled;
      });
      if (firstAvailableSize) firstAvailableSize.checked = true;
    }
  }

  syncSelection() {
    this.applyPackCountAvailability();

    var packCount = this.getSelectedPackCount();
    this.applySizeAvailability(packCount);

    var sizeInput = this.getCheckedInput(this.sizeInputs);
    var packVariant = sizeInput
      ? this.core.findPackVariant(
          this.packVariants,
          sizeInput.value,
          packCount,
        )
      : null;
    var sizeKey = sizeInput ? this.core.normalizeSize(sizeInput.value) : "";
    var info = this.availability[sizeKey];
    var ready = Boolean(
      sizeInput &&
        packCount &&
        packVariant &&
        packVariant.available &&
        info &&
        info.count >= packCount,
    );

    if (this.variantSelect) {
      this.variantSelect.value = ready ? String(packVariant.id) : "";
    }
    if (this.variantsInput) this.variantsInput.value = "";
    if (this.countInput) this.countInput.value = ready ? String(packCount) : "";

    this.updatePrice(packVariant);
    this.updateAddButton(ready);
  }

  updatePrice(packVariant) {
    if (this.priceEl && packVariant && packVariant.formatted_price) {
      this.priceEl.textContent = packVariant.formatted_price;
    }

    if (!this.comparePriceEl) return;

    var showComparePrice = Boolean(
      packVariant &&
        packVariant.compare_at_price &&
        Number(packVariant.compare_at_price) > Number(packVariant.price),
    );

    this.comparePriceEl.hidden = !showComparePrice;
    this.comparePriceEl.textContent = showComparePrice
      ? packVariant.formatted_compare_at_price
      : "";
  }

  updateAddButton(ready) {
    var soldOutText =
      (window.theme && theme.productStrings && theme.productStrings.soldOut) ||
      "Sold Out";

    if (this.addButton) this.addButton.disabled = !ready;
    if (this.addButtonText) {
      this.addButtonText.textContent = ready ? "ADD TO CART" : soldOutText;
    }
  }

  clearErrors() {
    if (!this.errorsEl) return;
    this.errorsEl.hidden = true;
    this.errorsEl.textContent = "";
  }

  showError(message) {
    if (!this.errorsEl) {
      console.warn(message);
      return;
    }

    this.errorsEl.hidden = false;
    this.errorsEl.textContent = message;
  }

  setLoading(isLoading) {
    if (!this.addButton) return;
    this.addButton.classList.toggle("btn--loading", isLoading);
    if (isLoading) this.addButton.disabled = true;
  }

  handleAddToCart() {
    if (this._adding) return;
    if (!window.jQuery || !window.theme || !theme.cart) {
      this.showError("Cart is not ready yet. Please refresh the page.");
      return;
    }

    this.clearErrors();
    this.syncSelection();

    var sizeInput = this.getCheckedInput(this.sizeInputs);
    var packCount = this.getSelectedPackCount();
    var packVariant = sizeInput
      ? this.core.findPackVariant(
          this.packVariants,
          sizeInput.value,
          packCount,
        )
      : null;
    var sizeKey = sizeInput ? this.core.normalizeSize(sizeInput.value) : "";
    var fulfillmentIds = this.core.pickFulfillmentIds(
      this.availability[sizeKey],
      packCount,
      this.fillRule,
      this.colorPriority,
    );

    if (!packVariant || !packVariant.available || fulfillmentIds.length !== packCount) {
      this.showError("This size and pack combination is not available right now.");
      return;
    }

    this.variantSelect.value = String(packVariant.id);
    this.variantsInput.value = fulfillmentIds.join(",");
    this.countInput.value = String(packCount);
    this.uidInput.value =
      Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
    this.quantityInput.value = "1";

    var $ = window.jQuery;
    var $form = $(this.form);

    this._adding = true;
    this.setLoading(true);

    theme.cart
      .addItemFromForm($form.serialize())
      .then(
        function () {
          this._adding = false;
          this.setLoading(false);
          this.syncSelection();
          $form.find(".errors").not("[data-mystery-pack-errors]").remove();
          $("body").trigger("added.ajaxProduct");
          $(".cart-popup").addClass("active");
        }.bind(this),
      )
      .catch(
        function (error) {
          this._adding = false;
          this.setLoading(false);
          this.syncSelection();

          var message =
            (error &&
              error.responseJSON &&
              (error.responseJSON.description || error.responseJSON.message)) ||
            "Could not add this Mystery Pack to cart.";

          this.showError(message);
          console.warn(message, error);
        }.bind(this),
      );
  }
}

customElements.get("mystery-pack-buy-box") ||
  customElements.define("mystery-pack-buy-box", MysteryPackForm);
